import * as React from 'react'
import { createPortal } from 'react-dom'
import { FaIcon, faCalendar } from '@/lib/fa'
import MonthPicker from '../date/MonthPicker'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { romanMonthKeys } from '@/modules/shared/constants/months'

const DEBUG = false




interface MonthPickerButtonProps {
  useRomanNumerals?: boolean
}

const MonthPickerButton: React.FC<MonthPickerButtonProps> = ({
  useRomanNumerals = false,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [open, setOpen] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      // Viewport-relative coords for position: fixed
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left })
    }
    setOpen(willOpen)
  }


  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current || !dropdownRef.current) return

    const margin = 8
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = dropdownRef.current.offsetWidth
    const dropdownHeight = dropdownRef.current.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.left
    if (left + dropdownWidth > viewportWidth - margin) {
      left = rect.right - dropdownWidth
    }
    if (left < margin) {
      left = margin
    }

    let top = rect.bottom + 4
    if (top + dropdownHeight > viewportHeight - margin) {
      const above = rect.top - dropdownHeight - 4
      if (above > margin) {
        top = above
      }
    }

    setDropdownPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    )
  }, [open])

  // Było `try { useDate() } catch { return null }` plus dwa strażniki - pozostałość
  // po czasach, gdy to był kontekst Reacta. Dziś `useDate` to selektor zustanda:
  // nie rzuca, nie zwraca `undefined`, a `try` wokół hooka psuł ich kolejność.
  const { month, setMonth } = useDate()

  const [formattedMonth, setFormattedMonth] = React.useState<string | null>(
    null,
  )

  React.useEffect(() => {
    if (!month) {
      setFormattedMonth(null)
      return
    }
    // Parse YYYY-MM format and convert to MM-YYYY
    // month is in YYYY-MM format (e.g., "2025-08")
    const parts = month.split('-')
    if (parts.length !== 2) {
      setFormattedMonth(null)
      return
    }
    const year = parseInt(parts[0], 10)
    const monthNum = parseInt(parts[1], 10)

    // Validate the values
    if (isNaN(year) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      setFormattedMonth(null)
      return
    }

    // Format based on useRomanNumerals prop
    if (useRomanNumerals) {
      // Format as year and Roman numeral month (e.g., "2025 VIII")
      const romanMonth = romanMonthKeys[monthNum - 1]
      const formatted = `${year} ${romanMonth}`
      setFormattedMonth(formatted)
    } else {
      // Format as YYYY-MM (e.g., "2025-08")
      const formatted = `${year}-${String(monthNum).padStart(2, '0')}`
      setFormattedMonth(formatted)
    }
  }, [month, useRomanNumerals])

  // Handle FaIcon - it might be an object in production, check for default or the component itself
  let IconComponent: any = FaIcon
  if (FaIcon && typeof FaIcon === 'object' && !React.isValidElement(FaIcon)) {
    // Try to get the actual component from the object
    IconComponent =
      (FaIcon as any).default || (FaIcon as any).FontAwesomeIcon || FaIcon
  }

  if (
    !IconComponent ||
    (typeof IconComponent !== 'function' && typeof IconComponent !== 'object')
  ) {
    console.error('[MonthPickerButton] FaIcon/IconComponent is invalid:', {
      FaIcon: typeof FaIcon,
      IconComponent: typeof IconComponent,
      FaIconValue: FaIcon,
    })
    // Fallback: render without icon
    return (
      <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className={`tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-base-300 tw-bg-transparent tw-px-2 tw-py-1 hover:tw-bg-[#F5F5F5] ${open && 'tw-border-2 tw-border-primary'}`}
        >
          <p className="tw-text-xs tw-text-content-base">
            {formattedMonth || 'Wybierz'}
          </p>
        </button>
        {open &&
          MonthPicker &&
          createPortal(
            <div
              ref={dropdownRef}
              className="tw-fixed tw-z-[200] tw-shadow-md"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
              }}
            >
              <MonthPicker
                value={month}
                onConfirm={(e) => {
                  ;(setMonth(e), setOpen(false))
                }}
              />
            </div>,
            document.body,
          )}
      </div>
    )
  }

  if (!MonthPicker) {
    console.error('[MonthPickerButton] MonthPicker is undefined')
    return null
  }

  return (
    <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-base-300 tw-bg-transparent tw-px-2 tw-py-1 hover:tw-bg-[#F5F5F5] ${open && 'tw-border-2 tw-border-primary'}`}
      >
        {React.createElement(IconComponent, {
          icon: faCalendar,
          className: 'tw-h-4 tw-w-4 tw-text-content-base',
          style: { fontWeight: 900, fontSize: '12px' },
        })}
        <p className="tw-text-xs tw-text-content-base">
          {formattedMonth || 'Wybierz'}
        </p>
      </button>

      {open &&
        MonthPicker &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[200] tw-shadow-md"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <MonthPicker
              value={month}
              onConfirm={(e) => {
                ;(setMonth(e), setOpen(false))
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}



// Wrap in error boundary
const SafeMonthPickerButton: React.FC<MonthPickerButtonProps> = (props) => {
  try {
    if (!MonthPickerButton || typeof MonthPickerButton !== 'function') {
      console.error(
        '[MonthPickerButton] Component is not a function:',
        typeof MonthPickerButton,
      )
      return null
    }
    return <MonthPickerButton {...props} />
  } catch (error) {
    console.error('[MonthPickerButton] Error rendering:', error)
    return null
  }
}

export default SafeMonthPickerButton
